import type { CSSProperties } from 'react';

/**
 * FDS fdsapp 공통 UI 토큰 — 기준 화면: PortfolioOverview · TradingJournal · PortfolioReview
 * 스킬: .claude/skills/fdsapp-ui/SKILL.md
 * 새 화면·Research·Quant 컴포넌트는 여기서 import. 인라인 임의 색상 금지.
 */

// ── 색상 ────────────────────────────────────────────────────
export const colors = {
  pageBg: '#eff6ff',
  cardBg: '#ffffff',
  cardBorder: '#3b82f6',
  cardBorderSub: '#8b5cf6',
  title: '#1e40af',
  titleSub: '#6d28d9',
  hint: '#9ca3af',
  body: '#4b5563',
  muted: '#6b7280',
  divider: '#e5e7eb',
  dividerLight: '#f3f4f6',
  btnPrimary: '#3b82f6',
  btnDisabled: '#9ca3af',
  inputBorder: '#d1d5db',
  up: '#dc2626',
  down: '#2563eb',
  flat: '#374151',
  buyBg: '#fee2e2',
  sellBg: '#dbeafe',
  rowHover: '#eff6ff',
  agHeader: '#f8fafc',
  agBorder: '#e2e8f0',
} as const;

// ── 레이아웃 ─────────────────────────────────────────────────
export const page: CSSProperties = {
  padding: '20px',
  backgroundColor: colors.pageBg,
  minHeight: '100vh',
};

export const card: CSSProperties = {
  backgroundColor: colors.cardBg,
  border: `2px solid ${colors.cardBorder}`,
  borderRadius: '8px',
  padding: '12px 16px',
  marginBottom: '16px',
};

export const cardGrid: CSSProperties = {
  ...card,
  padding: '16px',
  marginBottom: '20px',
};

export const cardSub: CSSProperties = {
  ...card,
  border: `2px solid ${colors.cardBorderSub}`,
};

export const titleRow: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '12px',
};

export const gridTitle: CSSProperties = {
  margin: 0,
  fontSize: '12px',
  fontWeight: 600,
  color: colors.title,
};

export const gridTitleSub: CSSProperties = {
  margin: 0,
  fontSize: '12px',
  fontWeight: 600,
  color: colors.titleSub,
};

export const gridHint: CSSProperties = {
  marginLeft: 6,
  fontSize: '11px',
  fontWeight: 400,
  color: colors.hint,
};

export const toolbarLabel: CSSProperties = {
  color: colors.title,
  fontSize: '12px',
  fontWeight: 600,
};

export const btnPrimary: CSSProperties = {
  padding: '5px 12px',
  backgroundColor: colors.btnPrimary,
  color: 'white',
  border: 'none',
  borderRadius: '3px',
  cursor: 'pointer',
  fontSize: '12px',
  fontWeight: 500,
};

export const selectSm: CSSProperties = {
  fontSize: '12px',
  padding: '4px 8px',
  border: `1px solid ${colors.inputBorder}`,
  borderRadius: '3px',
};

export const emptyMsg: CSSProperties = {
  fontSize: '12px',
  color: colors.hint,
  margin: '8px 0',
};

// ── 목록·게시판 (TradingJournal 패턴) ─────────────────────────
export const listRow = (accent?: string): CSSProperties => ({
  borderTop: `1px solid ${colors.divider}`,
  borderLeft: `3px solid ${accent ?? colors.divider}`,
  paddingLeft: 10,
});

export const listRowHeader: CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  gap: 10,
  padding: '8px 0',
  cursor: 'pointer',
  fontSize: '12px',
  flexWrap: 'wrap',
};

export const listRowDetail: CSSProperties = {
  padding: '4px 0 12px 22px',
};

// ── 숫자·부호 (한국 주식: 상승=빨강, 하락=파랑) ───────────────
export const signColor = (v?: number | null): string => {
  const n = v ?? 0;
  if (n > 0) return colors.up;
  if (n < 0) return colors.down;
  return colors.flat;
};

export const won = (v?: number | null): string => {
  if (v == null) return '-';
  const n = Math.round(v);
  return (n === 0 ? 0 : n).toLocaleString('ko-KR');
};

export const cost = (v?: number | null): string => {
  const n = Math.round(v ?? 0);
  return n === 0 ? '0' : (-n).toLocaleString('ko-KR');
};

export const pct = (v?: number | null, digits = 2): string =>
  v == null ? '-' : `${v >= 0 ? '+' : ''}${v.toFixed(digits)}%`;

// ── AG Grid ──────────────────────────────────────────────────
export const agGridBox = (height: number | string): CSSProperties => ({
  height,
  width: '100%',
});

// ── 벤토 대시보드 (2026-11-03) ───────────────────────────────
// 카드형 타일을 크기를 달리해 12열 그리드에 배치한다. 참고: bergside/awesome-design-skills 'bento' (MIT)
//   간격 4/8/12/16/24 · 글자 12/14/16/20/24 · 숫자는 tabular-nums
// 라이트는 위 colors 를 그대로 쓰고, 다크만 별도 팔레트를 둔다. 손익색(상승 빨강/하락 파랑)은 다크에서 밝게.
export interface BentoTokens {
  pageBg: string; tileBg: string; tileBorder: string; title: string; text: string; muted: string;
  hint: string; divider: string; accent: string; up: string; down: string; flat: string;
  upBg: string; downBg: string; chip: string;
}
export const bentoLight: BentoTokens = {
  pageBg: colors.pageBg, tileBg: colors.cardBg, tileBorder: '#dbeafe', title: colors.title, text: colors.flat,
  muted: colors.muted, hint: colors.hint, divider: colors.dividerLight, accent: colors.btnPrimary,
  up: colors.up, down: colors.down, flat: colors.flat, upBg: colors.buyBg, downBg: colors.sellBg, chip: '#eff6ff',
};
export const bentoDark: BentoTokens = {
  pageBg: '#0b1220', tileBg: '#111a2e', tileBorder: '#1e2a44', title: '#93c5fd', text: '#e5e7eb',
  muted: '#9ca3af', hint: '#6b7280', divider: '#1e2a44', accent: '#60a5fa',
  up: '#f87171', down: '#60a5fa', flat: '#d1d5db', upBg: 'rgba(248,113,113,0.15)', downBg: 'rgba(96,165,250,0.15)', chip: '#1e2a44',
};
export const bentoSign = (t: BentoTokens, v?: number | null): string => {
  const n = v ?? 0;
  return n > 0 ? t.up : n < 0 ? t.down : t.flat;
};
